import React, { useEffect, useState } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent,
  CircularProgress,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton
} from '@mui/material';
import { Close, PictureAsPdf, InsertDriveFile } from '@mui/icons-material';
import axios from 'axios';
import { BN_ARCHIVE_DOCUMENTS_URI } from '../../constants/constant';
import { getAccessToken } from '../../utils/okta-config';

const InvoiceDocumentModal = ({ open, onClose, invoiceNumber }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open && invoiceNumber) {
      fetchDocuments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, invoiceNumber]);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Use axios like other BillNow APIs with proper headers
      const response = await axios.get(`${BN_ARCHIVE_DOCUMENTS_URI}/`, {
        params: {
          invoiceNumber: invoiceNumber
        },
        headers: {
          Authorization: `Bearer ${getAccessToken()}`,
          "Content-Type": "application/json",
        },
      });
      
      // Response structure from BillNow backend
      const list = response.data?.data || [];
      if (response.status === 200 && list.length > 0) {
        setDocuments(list);
      } else {
        setError('No documents found for this invoice');
      }
    } catch (err) {
      console.error('Error fetching documents:', err);
      setError(err.response?.data?.message || 'Failed to fetch documents');
    } finally {
      setLoading(false);
    }
  };

  const openDocument = (doc) => {
    const url = doc?.url;
    if (url) {
      window.open(url, '_blank');
    }
  };

  const getDocumentIcon = (docType) => {
    const type = docType?.toUpperCase();
    if (type === 'PDF') {
      return <PictureAsPdf sx={{ color: '#d32f2f' }} />;
    } else {
      return <InsertDriveFile sx={{ color: '#757575' }} />;
    }
  };

  const getDocumentName = (index, totalDocs) => {
    if (index === 0) {
      return 'Billing Document PDF';
    } else if (index === 1) {
      return 'Billing Document PDF (Duplicate)';
    } else {
      return 'PDF';
    }
  };

  const handleClose = () => {
    setDocuments([]);
    setError(null);
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: '400px',
          maxHeight: '80vh'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        backgroundColor: '#f5f5f5',
        borderBottom: '1px solid #e0e0e0'
      }}>
        <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
          Documents for Invoice: {invoiceNumber}
        </Typography>
        <IconButton onClick={handleClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ mt: 2 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box textAlign="center" py={4}>
            <Typography color="error" variant="body1">{error}</Typography>
          </Box>
        ) : (
          <>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
              Related Documents
            </Typography>

            {documents.length > 0 ? (
              <List>
                {documents.map((doc, index) => (
                  <ListItem
                    key={index}
                    button
                    onClick={() => openDocument(doc)}
                    sx={{
                      borderRadius: 1,
                      mb: 1,
                      '&:hover': {
                        backgroundColor: '#f5f5f5',
                        boxShadow: 1,
                      },
                      border: '1px solid #e0e0e0',
                      cursor: 'pointer',
                    }}
                  >
                    <ListItemIcon>{getDocumentIcon(doc.docType)}</ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                          {getDocumentName(index, documents.length)}
                        </Typography>
                      }
                      secondary="Click to view document"
                    />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Box textAlign="center" py={4}>
                <Typography>No documents available</Typography>
              </Box>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default InvoiceDocumentModal;
